/**
 * ChatMarkdown — the one markdown document body for every chat surface.
 *
 * WHY THIS EXISTS (#908, epic #907). The markdown renderer lived inline in
 * `ChatBubble.tsx`, so five of the six chat surfaces could not reach it: the
 * widget's private message list, WilliamsPage, the handoff views and the ledger
 * panel all printed raw text while this renderer sat eighty lines away, unused.
 * It is a component now so adopting it is an import, not a copy.
 *
 * It also drops the `prose prose-sm dark:prose-invert` classes it used to carry.
 * `@tailwindcss/typography` is NOT installed, so those classes emitted nothing
 * at all — every chat answer has been rendering with browser-default heading
 * sizes and list indents since the feature shipped. Styling now comes from the
 * `.chat-prose` block in `index.css`, written against the `--stevens-*` token
 * layer so it flips with the theme (design-grammar §3) instead of a plugin we
 * never had.
 *
 * Two layout rules are load-bearing at 380px (the widget's compact width):
 * tables render inside their own `overflow-x-auto` wrapper and `<pre>` scrolls
 * on its own axis. Wide content must scroll, never widen its column — a table
 * that stretches the turn breaks the whole panel, not just itself.
 *
 * RICH CONTENT (#930, the chat-ui-direction §4 deferred vocabulary). One
 * renderer, every surface inherits, both densities:
 * - Callouts: GFM alert blockquotes (`> [!NOTE]` …) via `remarkCallouts` — no
 *   new dependency; tints from the chip token triples.
 * - Syntax highlighting: `rehype-highlight` with an explicit grammar registry,
 *   lazy-loaded on the first fenced turn (renderers/highlight.ts); the theme
 *   is CSS in `index.css` written against the token layer — no vendor
 *   stylesheet.
 * - Diagrams: ```mermaid fences; the mermaid library is lazy-loaded on first
 *   use (MermaidBlock) so the main bundle stays flat.
 * - Inline charts: ```chart fences with a small JSON spec (bar | line, ≤2
 *   series) via the existing recharts dep, itself lazy so `charts-vendor`
 *   stays off pages until a chart appears.
 * Every rich block degrades to the plain fenced code block — via explicit
 * fallback on invalid input and via RendererBoundary if a renderer throws. A
 * malformed block must never crash a turn.
 */
import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Link } from 'react-router-dom';
import { ExternalLink } from '@/components/ui/ExternalLink';
import { cn } from '@/lib/utils';
import type { MessageStatus } from '@/types/chat';
import { remarkCallouts, CALLOUT_KINDS, type CalloutKind } from './renderers/remarkCallouts';
import { Callout } from './renderers/Callout';
import { RendererBoundary } from './renderers/RendererBoundary';
import MermaidBlock from './renderers/MermaidBlock';

// recharts lives in the `charts-vendor` chunk; a static import here would make
// it a hard dependency of every page that mounts the widget (all of them).
const ChartBlock = lazy(() => import('./renderers/ChartBlock'));

type HighlightPlugin = (typeof import('./renderers/highlight'))['rehypeHighlightPlugin'];

// The configured rehype highlighter, loaded on the first turn that actually
// contains a fence and cached for every renderer after it. `rehype-highlight`
// statically imports lowlight's `common` grammar set, so importing it here
// would put ~183 kB min (+56 kB gzip) on the MAIN bundle — see
// renderers/highlight.ts. Until the chunk resolves, fenced code renders
// unhighlighted (correct degradation), then colorizes in place.
let cachedHighlight: HighlightPlugin | null = null;

function useHighlightPlugin(needed: boolean): HighlightPlugin | null {
  const [plugin, setPlugin] = useState<HighlightPlugin | null>(cachedHighlight);
  useEffect(() => {
    if (!needed || plugin) return;
    let cancelled = false;
    import('./renderers/highlight').then(
      (m) => {
        cachedHighlight = m.rehypeHighlightPlugin;
        if (!cancelled) setPlugin(cachedHighlight);
      },
      (reason) => {
        // Chunk failed to load (offline, deploy skew): keep plain code.
        // Degradation is correct, but it must be LOUD (#1047 / B-574): this
        // handler used to swallow the rejection entirely, which left the
        // Windows lazy-import test failures with nothing to read — a test saw
        // "highlighting never appeared" and no reason. The ChartBlock path
        // needs no twin: a rejected React.lazy chunk throws into
        // RendererBoundary, and React 19's default onCaughtError already
        // console.error's it with the boundary named (verified in-test).
        console.warn('[ChatMarkdown] lazy import of ./renderers/highlight failed — code renders unhighlighted', reason);
      },
    );
    return () => {
      cancelled = true;
    };
  }, [needed, plugin]);
  return plugin;
}

/**
 * Parent surfaces read editorially; staff surfaces (Williams / ledger /
 * handoff) run denser. One vocabulary, two densities — the standing
 * parent-vs-staff density philosophy, not a second design.
 */
export type ChatDensity = 'comfortable' | 'compact';

interface ChatMarkdownProps {
  content: string;
  /** When 'streaming', a caret follows the last rendered character. */
  status?: MessageStatus;
  density?: ChatDensity;
  className?: string;
}

/** Minimal hast shape — enough to read a fence's language and raw text. */
interface HastNode {
  type?: string;
  tagName?: string;
  value?: string;
  children?: HastNode[];
  properties?: { className?: unknown; dataCallout?: unknown };
}

function hastText(node: HastNode | undefined): string {
  if (!node) return '';
  if (node.type === 'text') return node.value ?? '';
  return (node.children ?? []).map(hastText).join('');
}

/** The fence language + raw source of a `<pre><code class="language-x">` node. */
function extractFence(node: HastNode | undefined): { lang: string | null; text: string } {
  const codeEl = node?.children?.find((c) => c.tagName === 'code');
  const classes = Array.isArray(codeEl?.properties?.className)
    ? (codeEl.properties.className as unknown[]).filter((c): c is string => typeof c === 'string')
    : [];
  const lang = classes.find((c) => c.startsWith('language-'))?.slice('language-'.length) ?? null;
  return { lang, text: hastText(codeEl).replace(/\n$/, '') };
}

export function ChatMarkdown({
  content,
  status,
  density = 'comfortable',
  className,
}: ChatMarkdownProps) {
  // A leading "1." is markdown for "start an ordered list", which turns an
  // answer that merely opens with a number into a list. Escaping the first one
  // only keeps real lists intact. Preserved verbatim from the inline renderer
  // this component replaces.
  const body = (content ?? '').replace(/^(\d+)\.([ \t\n]|$)/m, '$1\\.$2');

  // Only turns that carry a fence pay for the highlighter chunk at all.
  const highlight = useHighlightPlugin(body.includes('```'));

  return (
    <div
      className={cn(
        'chat-prose max-w-none break-words',
        density === 'compact' && 'chat-prose-compact',
        className,
      )}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkCallouts]}
        // `chart` / `mermaid` are not registered grammars, so the highlighter
        // leaves those fences as plain text for the pre dispatcher below.
        rehypePlugins={highlight ? [highlight] : []}
        components={{
          a: ({ href, children }) => {
            if (href?.startsWith('/')) {
              return (
                <Link
                  to={href}
                  className="inline-flex items-center gap-1 px-3 py-1 mt-1 rounded-full text-caption font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-colors no-underline"
                >
                  {children}
                </Link>
              );
            }
            return (
              <ExternalLink href={href ?? '#'} className="text-primary underline underline-offset-2 hover:no-underline">
                {children}
              </ExternalLink>
            );
          },
          // A GFM table is the widest thing an answer can contain. It scrolls
          // inside its own box so the reading column keeps its width.
          table: ({ children }) => (
            <div className="overflow-x-auto">
              <table>{children}</table>
            </div>
          ),
          // GFM alert blockquotes were tagged by remarkCallouts; everything
          // else stays an ordinary quote.
          blockquote: ({ node, children }) => {
            const kind = (node as HastNode | undefined)?.properties?.dataCallout;
            if (typeof kind === 'string' && (CALLOUT_KINDS as readonly string[]).includes(kind)) {
              return <Callout kind={kind as CalloutKind}>{children}</Callout>;
            }
            return <blockquote>{children}</blockquote>;
          },
          // Fenced code: `chart` and `mermaid` get rich renderers, everything
          // else scrolls on its own axis (one long line must not widen the
          // turn). The plain block is ALSO each rich renderer's fallback —
          // invalid spec, renderer crash, or a chunk still loading.
          pre: ({ node, children }) => {
            const plain: ReactNode = <pre className="overflow-x-auto">{children}</pre>;
            const { lang, text } = extractFence(node as HastNode | undefined);
            if (lang === 'chart') {
              return (
                <RendererBoundary fallback={plain} resetKey={text}>
                  <Suspense fallback={plain}>
                    <ChartBlock code={text} compact={density === 'compact'} fallback={plain} />
                  </Suspense>
                </RendererBoundary>
              );
            }
            if (lang === 'mermaid') {
              return (
                <RendererBoundary fallback={plain} resetKey={text}>
                  <MermaidBlock code={text} fallback={plain} />
                </RendererBoundary>
              );
            }
            return plain;
          },
        }}
      >
        {body}
      </ReactMarkdown>
      {status === 'streaming' && (
        <span className="inline-block w-1.5 h-4 ml-0.5 bg-current animate-pulse rounded-sm" />
      )}
    </div>
  );
}
